import { useCallback, useMemo } from 'react';
import {
  moveItem,
  orderByCalendar,
  toOrderItems,
  toggleHidden,
  type CalendarOrdered,
} from './calendarOrder';
import type { CalendarOrderEditor } from './useCalendarOrderEditor';

interface Result<R> {
  ordered: CalendarOrdered<R>[];
  onMove: (activeId: number, overId: number) => void;
  onToggleHidden: (technicianId: number) => void;
}

/**
 * Rows in the Leitung's order, plus the two edits the board can make. Shared by the day and the
 * week widget so they cannot disagree about either.
 */
export function useCalendarRows<R extends { technicianId: number; calendarHidden: boolean }>(
  rows: R[],
  editor: CalendarOrderEditor,
): Result<R> {
  const { draft, setDraft } = editor;

  const ordered = useMemo(() => orderByCalendar(rows, draft), [rows, draft]);

  // The draft starts as the server's order, so the first edit has something to change.
  const current = useCallback(() => draft ?? toOrderItems(rows), [draft, rows]);

  const onMove = useCallback(
    (activeId: number, overId: number) => setDraft(moveItem(current(), activeId, overId)),
    [current, setDraft],
  );

  const onToggleHidden = useCallback(
    (technicianId: number) => setDraft(toggleHidden(current(), technicianId)),
    [current, setDraft],
  );

  return { ordered, onMove, onToggleHidden };
}
